import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Frame, Head } from "../components/spec/Frame";
import { useArticles } from "../lib/hooks";
import { requestRepack } from "../frames";
import { INSIGHTS_PAGE } from "../data/content";

const fmtDate = (d) => {
  if (!d) return "";
  const t = new Date(d);
  return isNaN(t) ? "" : t.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

const ArticleRow = ({ a }) => (
  <Link
    to={`/insights/${a.slug}`}
    data-pg-item
    data-testid={`insight-card-${a.slug}`}
    className="group bg-[#0a0a0a] hover:bg-[#141414] p-6 flex flex-col transition-colors"
  >
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] tracking-[0.14em] uppercase">
      <span className="text-[#e60000]">{a.category}</span>
      {fmtDate(a.created_at) && <span className="text-white/50">{fmtDate(a.created_at)}</span>}
    </div>
    <h3 className="mt-3 font-display text-lg lg:text-xl font-bold text-white leading-snug group-hover:text-[#e60000] transition-colors">{a.title}</h3>
    <p className="mt-3 text-sm text-white/70 leading-relaxed flex-1">{a.excerpt}</p>
    <span className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.16em] uppercase text-white group-hover:text-[#e60000]">
      Read article <ArrowUpRight className="h-4 w-4" />
    </span>
  </Link>
);

export default function Insights() {
  const { data: articles = [], isLoading } = useArticles();
  useEffect(() => {
    document.title = "Insights — REGERA";
  }, []);
  useEffect(() => { requestRepack(); }, [isLoading, articles.length]);

  const { intro, featured } = INSIGHTS_PAGE;
  const norm = (s) => (s || "").toLowerCase().replace(/[^a-z]/g, "");
  const feat = featured.items
    .map((f) => ({ ...f, article: articles.find((a) => norm(a.title) === norm(f.title)) }))
    .filter((f) => f.article);
  const featSlugs = new Set(feat.map((f) => f.article.slug));
  const others = articles.filter((a) => !featSlugs.has(a.slug));

  return (
    <div data-testid="insights-page">
      <Frame id="insights-intro" first>
        <Head as="h1" size="lg" {...intro} />
      </Frame>
      <Frame id="insights-featured">
        <Head overline={featured.overline} title={featured.title} />
        <div className="mt-8 grid lg:grid-cols-2 gap-6">
          {(isLoading ? featured.items : feat).map((f) => (
            <Link
              key={f.title}
              to={f.article ? `/insights/${f.article.slug}` : "/insights"}
              data-pg-item
              data-testid={`insight-featured-${f.article ? f.article.slug : "loading"}`}
              className="group border border-white/10 bg-[#111] hover:border-[#e60000]/60 p-7 lg:p-9 flex flex-col transition-colors"
            >
              {f.article && (
                <div className="flex flex-wrap gap-3 text-[11px] tracking-[0.14em] uppercase">
                  <span className="text-[#e60000]">{f.article.category}</span>
                  <span className="text-white/50">{fmtDate(f.article.created_at)}</span>
                </div>
              )}
              <h3 className="mt-4 font-display text-2xl lg:text-3xl font-bold text-white leading-tight group-hover:text-[#e60000] transition-colors">{f.title}</h3>
              <p className="mt-4 text-white/75 leading-relaxed flex-1">{f.text}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-xs tracking-[0.16em] uppercase text-white group-hover:text-[#e60000]">
                Read article <ArrowUpRight className="h-4 w-4" />
              </span>
            </Link>
          ))}
        </div>
      </Frame>
      {others.length > 0 && (
        <Frame id="insights-all">
          <Head overline="Published articles" title="More perspectives." />
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {others.map((a) => <ArticleRow key={a.slug} a={a} />)}
          </div>
        </Frame>
      )}
    </div>
  );
}
